import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { message, Modal, Spin, Steps } from 'antd';

import { cycloneApiClient } from '@/shared/api/core/axios-instance';
import type {
  CreateDeploymentRequest,
  DeploymentDetailResponse,
  DeploymentStatus,
  ModelConfig,
} from '@/shared/api/deploy';
import { createDeployment, updateDeployment } from '@/shared/api/deploy';
import { MODAL_FOOTER_BUTTON_SIZE } from '@/shared/lib/constants';
import { formatBytes } from '@/shared/lib/utils/number';
import { Button } from '@/shared/ui/Button';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useDeploymentDetail } from '@/features/deployment/model';

import styles from './DeploymentModal.module.css';
import { useDeploymentModalState } from './hooks/useDeploymentModalState';

type StatusBannerVariant = 'neutral' | 'progress' | 'failed' | 'success';

type ArtifactFile = {
  path: string;
  isDir: boolean;
  fileSize?: number;
};

type ArtifactInfo = {
  description?: string;
  files: ArtifactFile[];
};

type ModelInfo = {
  modelId: string;
  modelFormat?: string | null;
  servingConfig?: {
    backend?: string | null;
    convertToOnnx?: boolean | null;
  } | null;
};

type RunInfo = {
  runId: string;
  model: ModelInfo;
  modelName: string;
  artifact: ArtifactInfo;
};

type DeploymentState = {
  existingDeploymentId?: string;
  currentStatus: DeploymentStatus | null;
  currentStep: number;
  detail: DeploymentDetailResponse | null;
  inProgress: boolean;
  loadingDetail: boolean;
};

type DeploymentCallbacks = {
  onComplete?: (deploymentId: string) => void;
  onDeployingChange?: (isDeploying: boolean) => void;
  onStatusChange?: (status: DeploymentStatus) => void;
  onLogAdd?: (log: string) => void;
  onStepChange?: (step: number) => void;
};

type DeploymentModalProps = {
  open: boolean;
  onClose: () => void;
  mode?: 'deploy' | 'start' | 'stop';
  runInfo: RunInfo;
  deploymentState: DeploymentState;
  onDeploymentChange?: DeploymentCallbacks;
  stopAcknowledgementOnly?: boolean;
  startAcknowledgementOnly?: boolean;
};

export const DeploymentModal: React.FC<DeploymentModalProps> = ({
  open,
  onClose,
  mode = 'deploy',
  runInfo,
  deploymentState,
  onDeploymentChange,
  stopAcknowledgementOnly = false,
  startAcknowledgementOnly = false,
}) => {
  const { runId, model, modelName, artifact } = runInfo;
  const {
    existingDeploymentId,
    currentStatus: currentDeploymentStatus,
    currentStep: currentDeploymentStep,
    detail: externalDetail,
    inProgress: isDeploymentInProgress,
    loadingDetail: externalLoadingDetail,
  } = deploymentState;
  const {
    onComplete: onDeploymentComplete,
    onDeployingChange,
    onStatusChange: _onServingStatusChange,
    onLogAdd,
    onStepChange: _onStepChange,
  } = onDeploymentChange || {};
  const {
    data: queryDeploymentDetail,
    isLoading: isQueryLoadingDetail,
    refetch: refetchDeploymentDetail,
  } = useDeploymentDetail(existingDeploymentId || null, {
    enabled:
      open &&
      !!existingDeploymentId &&
      mode !== 'deploy' &&
      !(mode === 'stop' && stopAcknowledgementOnly) &&
      !(mode === 'start' && startAcknowledgementOnly),
  });

  const modalState = useDeploymentModalState({
    open,
    mode,
    currentStatus: currentDeploymentStatus,
    onLogAdd,
  });

  const { isDeploying, setIsDeploying, hasRefetchedAfterPackagingRef, addLog, resetState } = modalState;

  const { data: artifactData, isLoading: isLoadingArtifacts } = useQuery<ArtifactFile[]>({
    queryKey: ['run-artifacts', runId],
    queryFn: async () => {
      const response = await cycloneApiClient.get(`/assets/${runId}`, {
        params: { type: 'run' },
      });
      return response.data?.artifacts?.files || [];
    },
    enabled: open && !!runId,
    staleTime: 5 * 60 * 1000,
  });

  const actualArtifactFiles: ArtifactFile[] = artifactData || [];

  const currentStatus = currentDeploymentStatus;
  const currentStep = currentDeploymentStep;
  const deploymentDetail = externalDetail || queryDeploymentDetail;
  const isLoadingDetail = externalLoadingDetail || isQueryLoadingDetail;

  useEffect(() => {
    if (!open && !isDeploymentInProgress) {
      resetState();
    }
  }, [open, isDeploymentInProgress, resetState]);

  useEffect(() => {
    if (mode === 'start' && currentStatus === 'serving_active' && isDeploying) {
      setIsDeploying(false);
      onDeployingChange?.(false);
    }
  }, [mode, currentStatus, isDeploying, onDeployingChange, setIsDeploying]);

  useEffect(() => {
    if (
      open &&
      mode === 'deploy' &&
      currentStep >= 1 &&
      existingDeploymentId &&
      !hasRefetchedAfterPackagingRef.current
    ) {
      hasRefetchedAfterPackagingRef.current = true;
      void refetchDeploymentDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, currentStep, existingDeploymentId, refetchDeploymentDetail]);

  const formatFilePath = (path: string): string => {
    const artifactsIndex = path.indexOf('/artifacts/');
    if (artifactsIndex !== -1) {
      return path.substring(artifactsIndex + '/artifacts/'.length);
    }
    return path;
  };

  const inProgress =
    isDeploying ||
    isDeploymentInProgress ||
    currentStatus === 'deployment_in_progress' ||
    currentStatus === 'serving_in_progress' ||
    currentStatus === 'deployment_succeeded';

  const getStatusBanner = (): { variant: StatusBannerVariant; message: string } => {
    if (mode === 'stop' && stopAcknowledgementOnly) {
      return {
        variant: 'success',
        message: '모델 서빙을 비활성화 했습니다',
      };
    }

    if (currentStatus === 'deployment_failed') {
      return { variant: 'failed', message: '배포 중 오류가 발생했습니다' };
    }

    if (currentStatus === 'serving_failed') {
      return { variant: 'failed', message: '서빙 중 오류가 발생했습니다' };
    }

    if (mode === 'deploy' && currentStatus === 'serving_active') {
      return { variant: 'success', message: '배포를 완료하여, 모델 서빙이 시작되었습니다' };
    }
    if (mode === 'start' && currentStatus === 'serving_active' && !isDeploying) {
      return { variant: 'success', message: '모델 서빙을 활성화 했습니다' };
    }

    if ((mode === 'deploy' || mode === 'start') && inProgress) {
      const currentStepIndex = mode === 'start' ? 2 : currentStep >= 3 ? 2 : Math.max(0, Math.min(currentStep, 2));
      const stepMessages = ['패키징을 진행 중입니다', '배포를 진행 중입니다', '서빙을 진행 중입니다'];
      return {
        variant: 'progress',
        message: stepMessages[currentStepIndex],
      };
    }

    if (mode === 'start') {
      return {
        variant: 'neutral',
        message: '다음의 런 산출물에 대한 모델 서빙을 시작합니다',
      };
    }
    return {
      variant: 'neutral',
      message: '다음의 런 산출물을 배포하여, 모델 서빙을 시작합니다',
    };
  };

  const statusBanner = getStatusBanner();

  const renderStatusBanner = () => (
    <div
      className={
        statusBanner.variant === 'neutral'
          ? styles.statusMessageBanner
          : `${styles.statusMessageBanner} ${styles[`statusMessageBanner_${statusBanner.variant}`]}`
      }>
      {statusBanner.variant !== 'neutral' && (
        <span className={styles.statusMessageIcon}>
          {statusBanner.variant === 'progress' && <Spin size="small" indicator={<LoadingOutlined spin />} />}
          {statusBanner.variant === 'failed' && <Icon name="fail" size={16} className={styles.bannerIcon} />}
          {statusBanner.variant === 'success' && <Icon name="circle-check" size={16} className={styles.bannerIcon} />}
        </span>
      )}
      <span className={styles.statusMessageText}>{statusBanner.message}</span>
    </div>
  );

  const isDeploymentComplete =
    (mode === 'deploy' && currentStatus === 'serving_active') ||
    (mode === 'start' && currentStatus === 'serving_active' && !isDeploying) ||
    (mode === 'stop' && stopAcknowledgementOnly) ||
    (mode === 'start' && startAcknowledgementOnly);

  const isFailed = currentStatus === 'deployment_failed' || currentStatus === 'serving_failed';

  const isServingReady =
    currentStatus === 'serving_active' ||
    currentStatus === 'serving_inactive' ||
    currentStatus === 'deployment_failed' ||
    currentStatus === 'serving_failed';

  const getServiceInfo = () => ({
    httpEndpoint: deploymentDetail?.endpoints?.http || deploymentDetail?.metadata?.endpoints?.http || 'N/A',
    grpcEndpoint: deploymentDetail?.endpoints?.grpc || deploymentDetail?.metadata?.endpoints?.grpc || 'N/A',
    metricsEndpoint: deploymentDetail?.endpoints?.metrics || deploymentDetail?.metadata?.endpoints?.metrics || 'N/A',
    modelName: deploymentDetail?.model_name || 'N/A',
    modelVersion: deploymentDetail?.model_version || 'N/A',
    namespace: deploymentDetail?.namespace || 'N/A',
    environment: deploymentDetail?.environment || 'N/A',
    replicas: deploymentDetail?.replicas || 1,
    runtimeMode: deploymentDetail?.runtime_mode || 'N/A',
    cpu: deploymentDetail?.cpu || 'N/A',
    memory: deploymentDetail?.memory || 'N/A',
  });

  const handleConfirm = useCallback(async () => {
    if (isDeploymentComplete) {
      onClose();
      return;
    }

    if (mode === 'stop') {
      onClose();
      return;
    }

    setIsDeploying(true);

    addLog(mode === 'start' ? '서빙 활성화 요청을 준비하고 있습니다...' : '패키징 요청을 준비하고 있습니다...');

    if (onDeployingChange) {
      onDeployingChange(true);
    }

    try {
      if (mode === 'deploy') {
        const deploymentName = `run-${runId}-${Date.now()}`;

        const sanitizedModelName = modelName
          .replace(/[^a-zA-Z0-9-_.]/g, '-')
          .replace(/^[^a-zA-Z0-9]+/, '')
          .replace(/[^a-zA-Z0-9]+$/, '')
          .toLowerCase();

        const modelConfig: ModelConfig = {
          model_name: sanitizedModelName,
          model_version: '1',
          model_id: model.modelId,
          instance_count: 1,
          ...(model.modelFormat && { model_format: model.modelFormat }),
        };

        const { backend, convertToOnnx } = model.servingConfig ?? {};

        const request: CreateDeploymentRequest = {
          deployment_name: deploymentName,
          run_id: runId,
          environment: 'dev',
          server_type: 'triton',
          runtime_mode: 'cpu',
          replicas: 1,
          models: [modelConfig],
          ...(backend && { backend }),
          ...(convertToOnnx != null && { convert_to_onnx: convertToOnnx }),
        };

        addLog('패키징 요청 중...');
        const response = await createDeployment(request);

        addLog(`✅ 패키징 요청 성공 (ID: ${response.deployment_id})`);
        addLog('상태 업데이트를 기다리는 중...');

        onDeploymentComplete?.(response.deployment_id);
      } else if (mode === 'start') {
        if (!existingDeploymentId) {
          message.error('배포 ID를 찾을 수 없습니다');
          setIsDeploying(false);
          onDeployingChange?.(false);
          return;
        }

        addLog('서빙 활성화 요청 중...');

        await updateDeployment(existingDeploymentId, { replicas: 1 });

        addLog('✅ 서빙 활성화 요청 완료');
        addLog('서빙 활성화 진행 상태를 확인하는 중...');
      }
    } catch (error) {
      console.error('[DeploymentModal] Deployment failed:', error);
      const failLabel = mode === 'start' ? '서빙 활성화' : '배포';
      addLog(`❌ ${failLabel} 요청에 실패했습니다`);
      setIsDeploying(false);
      onDeployingChange?.(false);
    }
  }, [
    isDeploymentComplete,
    onClose,
    mode,
    addLog,
    onDeployingChange,
    runId,
    modelName,
    model,
    onDeploymentComplete,
    existingDeploymentId,
    setIsDeploying,
  ]);

  const hasAutoStartedRef = useRef(false);
  useEffect(() => {
    if (open && mode === 'start' && !isDeploying && !hasAutoStartedRef.current && !startAcknowledgementOnly) {
      hasAutoStartedRef.current = true;
      handleConfirm();
    }
    if (!open) {
      hasAutoStartedRef.current = false;
    }
  }, [open, mode, isDeploying, handleConfirm, startAcknowledgementOnly]);

  const onlyFiles =
    actualArtifactFiles.length > 0
      ? actualArtifactFiles.filter((f) => !f.isDir)
      : artifact.files.filter((f) => !f.isDir);

  const stepsCurrent = useMemo(() => {
    if (mode === 'start') {
      if (!startAcknowledgementOnly || inProgress || currentStep >= 0) return 2;
      return -1;
    }
    return currentStep >= 3 ? 2 : Math.max(0, Math.min(currentStep, 2));
  }, [mode, currentStep, inProgress, startAcknowledgementOnly]);

  const surfaceLevel1 = 'var(--opds-sem-color-surface-standard-level1)';

  const renderStepIcon = (index: number): React.ReactNode | undefined => {
    if (mode === 'start' && !startAcknowledgementOnly && currentStep === -1) {
      if (index < 2) return undefined;
      if (index === 2) return <LoadingOutlined spin style={{ color: surfaceLevel1, fontSize: 16 }} />;
      return <span className={styles.stepWaitIndex}>{index + 1}</span>;
    }

    if (currentStep === -1) {
      return <span className={styles.stepWaitIndex}>{index + 1}</span>;
    }

    if (isFailed) {
      return <span className={styles.stepWaitIndex}>{index + 1}</span>;
    }

    const allDone = currentStep >= 3 || currentStatus === 'serving_active';
    if (allDone) {
      return undefined;
    }
    if (index < stepsCurrent) {
      return undefined;
    }
    if (index === stepsCurrent) {
      return <LoadingOutlined spin style={{ color: surfaceLevel1, fontSize: 16 }} />;
    }
    return <span className={styles.stepWaitIndex}>{index + 1}</span>;
  };

  const stepTitles = ['패키징', '배포', '서빙'];

  const deploymentStepsItems = [0, 1, 2].map((index) => {
    const icon = renderStepIcon(index);
    return {
      title: stepTitles[index],
      ...(icon !== undefined && icon !== null ? { icon } : {}),
    };
  });

  const serviceInfo = getServiceInfo();

  const shouldShowServiceInfo = !!deploymentDetail && currentStatus === 'serving_active';

  return (
    <Modal
      open={open}
      onOk={handleConfirm}
      onCancel={onClose}
      closable={true}
      footer={
        isDeploymentComplete ? (
          <Button
            variant="filled"
            color="standard"
            className={styles.modalConfirmButton}
            onClick={onClose}
            {...MODAL_FOOTER_BUTTON_SIZE}>
            확인
          </Button>
        ) : (
          <>
            <Button variant="outlined" color="standard" onClick={onClose} {...MODAL_FOOTER_BUTTON_SIZE}>
              닫기
            </Button>
            <Button
              variant="filled"
              color="standard"
              className={styles.modalConfirmButton}
              prefix={inProgress ? <LoadingOutlined spin /> : isFailed ? <Icon name="reset" size={16} /> : undefined}
              onClick={handleConfirm}
              disabled={
                inProgress || isLoadingDetail || (mode === 'deploy' && currentStatus !== null && !isServingReady)
              }
              {...MODAL_FOOTER_BUTTON_SIZE}>
              {isFailed ? '다시 시도' : inProgress ? '처리중' : '시작'}
            </Button>
          </>
        )
      }
      maskClosable={false}
      keyboard={false}
      title={
        <span className={styles.modalTitle}>
          {mode === 'stop' ? '모델 서빙 비활성화' : mode === 'start' ? '모델 서빙 활성화' : '모델 배포'}
        </span>
      }
      width={900}
      style={{ top: 40 }}
      rootClassName={styles.deploymentModalRoot}
      styles={{
        content: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        },
      }}>
      <div className={styles.container}>
        {mode === 'stop' && <div className={styles.topSection}>{renderStatusBanner()}</div>}
        {(mode === 'deploy' || mode === 'start') && (
          <div className={`${styles.section} ${styles.topSection}`}>
            <div className={styles.statusBox}>
              <Steps
                className={styles.deploymentSteps}
                labelPlacement="vertical"
                current={stepsCurrent}
                status={
                  currentStatus === 'deployment_failed' || currentStatus === 'serving_failed'
                    ? 'error'
                    : currentStep >= 3
                      ? 'finish'
                      : 'process'
                }
                items={deploymentStepsItems}
              />
            </div>
            {renderStatusBanner()}
          </div>
        )}

        <div className={styles.scrollableRegion}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>런 산출물</h3>
            {artifact.description && <div className={styles.artifactDescription}>{artifact.description}</div>}
            <div className={styles.artifactBox}>
              {isLoadingArtifacts ? (
                <div className={styles.artifactBoxLoading}>
                  <Spin />
                </div>
              ) : onlyFiles.length > 0 ? (
                onlyFiles.map((file, index) => (
                  <div key={index} className={styles.fileRow}>
                    <div className={styles.fileNameCell}>
                      <EllipsisText text={formatFilePath(file.path)} className={styles.fileNameText} />
                    </div>
                    {file.fileSize && (
                      <div className={styles.fileSizeCell}>
                        <EllipsisText text={formatBytes(file.fileSize)} className={styles.fileSize} />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.emptyMessage}>파일이 없습니다</div>
              )}
            </div>
          </div>

          {shouldShowServiceInfo && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>서빙 인터페이스 정보</h3>
              {isLoadingDetail ? (
                <div className={styles.infoBox}>
                  <LoadingState className={styles.loadingContainer} />
                </div>
              ) : (
                <div className={styles.infoBox}>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="HTTP Endpoint" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={serviceInfo.httpEndpoint}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="gRPC Endpoint" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={serviceInfo.grpcEndpoint}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="Model Name" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={serviceInfo.modelName}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="Model Version" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={serviceInfo.modelVersion}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="Environment" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={`${serviceInfo.environment} (${serviceInfo.namespace})`}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="Replicas" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={String(serviceInfo.replicas)}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="Runtime" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={serviceInfo.runtimeMode.toUpperCase()}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabelCell}>
                      <EllipsisText text="Resources" className={styles.infoLabel} tooltipPlacement="topLeft" />
                    </div>
                    <div className={styles.infoValueCell}>
                      <EllipsisText
                        text={`CPU: ${serviceInfo.cpu}, Memory: ${serviceInfo.memory}`}
                        className={styles.infoValue}
                        tooltipPlacement="topRight"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
